
import React, { useState, useEffect } from 'react';
import { 
    Table, Button, message, Tag, Space, Tooltip, Input, Dropdown, Menu, 
    Modal, Form, Select, Card, Row, Col, InputNumber, Popconfirm
} from 'antd';
import { 
    EditOutlined, DeleteOutlined, EllipsisOutlined, PlusOutlined, 
    EyeOutlined, CopyOutlined, DownloadOutlined, ReloadOutlined,
    FileTextOutlined, BarcodeOutlined, DollarOutlined
} from '@ant-design/icons';
import { useNavigate } from "react-router-dom";
import api from "../../components/api/api-base";
import { useBusinessContext } from '../../components/BusinessContext';
import { useAuthContext } from '../../components/AuthProvider';
import moment from 'moment';

const { Option } = Select;
const { TextArea } = Input;

const Rawmaterial = () => {
    const [loading, setLoading] = useState(false);
    const [rawMaterials, setRawMaterials] = useState([]);
    const [filteredMaterials, setFilteredMaterials] = useState([]);
    const [searchText, setSearchText] = useState('');
    
    // Modal states
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isViewModalVisible, setIsViewModalVisible] = useState(false);
    const [selectedMaterial, setSelectedMaterial] = useState(null);
    
    // Forms
    const [form] = Form.useForm();
    
    const navigate = useNavigate();
    const { businessId, businessName } = useBusinessContext();
    const { token } = useAuthContext();

    const unitOfMeasureOptions = [
        "kg", "g", "lb", "oz",
        "L", "mL", "gal", "fl oz",
        "m", "cm", "in", "ft",
        "piece", "dozen", "box", "roll",
        "sheet", "pack", "set", "unit"
    ];

    useEffect(() => {
        if (businessId) {
            fetchRawMaterials();
        }
    }, [businessId]);

    const fetchRawMaterials = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/rawmaterials/business/${businessId}`);
            console.log("Fetched raw materials:", response.data);
            setRawMaterials(response.data || []);
            setFilteredMaterials(response.data || []);
        } catch (error) {
            console.error("Fetch Error:", error);
            message.error(`Failed to fetch raw materials: ${error.message || 'Unknown error'}`);
            setRawMaterials([]);
            setFilteredMaterials([]);
        } finally {
            setLoading(false);
        }
    };

    const truncateText = (text, maxLength) => {
        if (text && text.length > maxLength) {
            return text.substring(0, maxLength) + '...';
        }
        return text;
    };

    const handleSearch = (value) => {
        setSearchText(value);
        const filtered = rawMaterials.filter(
            (material) =>
                material.name?.toLowerCase().includes(value.toLowerCase()) ||
                material.code?.toLowerCase().includes(value.toLowerCase()) ||
                material.description?.toLowerCase().includes(value.toLowerCase()) ||
                material.supplierPartNumber?.toLowerCase().includes(value.toLowerCase())
        );
        setFilteredMaterials(filtered);
    };

    const handleAdd = () => {
        setSelectedMaterial(null);
        form.resetFields();
        form.setFieldsValue({
            estimatedUnitCost: 0
        });
        setIsModalVisible(true);
    };

    const handleEdit = (record) => {
        setSelectedMaterial(record);
        form.setFieldsValue({
            code: record.code,
            name: record.name,
            description: record.description,
            supplierPartNumber: record.supplierPartNumber,
            baseUnitOfMeasure: record.baseUnitOfMeasure,
            estimatedUnitCost: record.estimatedUnitCost
        });
        setIsModalVisible(true);
    };

    const handleView = (record) => {
        setSelectedMaterial(record);
        setIsViewModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            await api.delete(`/rawmaterials/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            message.success("Raw material deleted successfully!");
            fetchRawMaterials();
        } catch (error) {
            console.error("Delete Error:", error);
            message.error(`Failed to delete: ${error.message || 'Unknown error'}`);
        }
    };

    const handleDuplicate = (record) => {
        setSelectedMaterial(null);
        form.setFieldsValue({
            code: `${record.code}-COPY`,
            name: `${record.name} (Copy)`,
            description: record.description,
            supplierPartNumber: record.supplierPartNumber,
            baseUnitOfMeasure: record.baseUnitOfMeasure,
            estimatedUnitCost: record.estimatedUnitCost
        });
        setIsModalVisible(true);
    };

    const handleModalOk = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            const payload = {
                businessId: businessId,
                code: values.code,
                name: values.name,
                description: values.description || '',
                supplierPartNumber: values.supplierPartNumber || '',
                baseUnitOfMeasure: values.baseUnitOfMeasure,
                estimatedUnitCost: values.estimatedUnitCost ? parseFloat(values.estimatedUnitCost) : 0
            };

            if (selectedMaterial) {
                await api.put(`/rawmaterials/${selectedMaterial.id}`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                message.success("Raw material updated successfully!");
            } else {
                await api.post(`/rawmaterials`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                message.success("Raw material created successfully!");
            }

            setIsModalVisible(false);
            form.resetFields();
            setSelectedMaterial(null);
            fetchRawMaterials();
        } catch (error) {
            console.error("Save Error:", error);
            if (error.response?.data?.errors) {
                const errors = error.response.data.errors;
                Object.keys(errors).forEach(key => {
                    message.error(`${key}: ${errors[key].join(', ')}`);
                });
            } else {
                message.error(`Failed to save: ${error.message || 'Unknown error'}`);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleModalCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
        setSelectedMaterial(null);
    };

    const handleExport = () => {
        const data = filteredMaterials.map(m => ({
            Code: m.code,
            Name: m.name,
            Description: m.description,
            'Supplier Part': m.supplierPartNumber,
            UOM: m.baseUnitOfMeasure,
            'Est. Cost': m.estimatedUnitCost
        }));
        console.log("Export data:", data);
        message.success("Export feature coming soon");
    };

    const getUomTagColor = (uom) => {
        const colorMap = {
            'kg': 'blue', 'g': 'blue', 'lb': 'blue', 'oz': 'blue',
            'L': 'green', 'mL': 'green', 'gal': 'green', 'fl oz': 'green',
            'm': 'purple', 'cm': 'purple', 'in': 'purple', 'ft': 'purple',
            'piece': 'orange', 'dozen': 'orange', 'box': 'orange', 'roll': 'orange'
        };
        return colorMap[uom] || 'default';
    };

    const columns = [
        {
            title: 'Code',
            dataIndex: 'code',
            key: 'code',
            width: 120,
            render: (text) => <Tag color="blue">{text}</Tag>,
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <Button type="link" onClick={() => handleView(record)} style={{ padding: 0 }}>
                    <strong>{text}</strong>
                </Button>
            ),
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            render: (text) => (
                <Tooltip title={text}>
                    {truncateText(text, 30)}
                </Tooltip>
            ),
        },
        {
            title: 'Supplier Part #',
            dataIndex: 'supplierPartNumber',
            key: 'supplierPartNumber',
            render: (text) => text ? (
                <Tag icon={<BarcodeOutlined />} color="purple">{text}</Tag>
            ) : '-',
        },
        {
            title: 'UOM',
            dataIndex: 'baseUnitOfMeasure',
            key: 'baseUnitOfMeasure',
            render: (text) => (
                <Tag color={getUomTagColor(text)}>{text}</Tag>
            ),
        },
        {
            title: 'Est. Cost',
            dataIndex: 'estimatedUnitCost',
            key: 'estimatedUnitCost',
            render: (cost) => cost ? `₵${cost.toFixed(2)}` : '-',
        },
        {
            title: 'Date Created',
            dataIndex: 'createdDate',
            key: 'createdDate',
            render: (date) => date ? moment(date).format('YYYY-MM-DD') : '-',
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 80,
            render: (_, record) => (
                <Dropdown
                    overlay={
                        <Menu>
                            <Menu.Item key="view" onClick={() => handleView(record)}>
                                <EyeOutlined /> View Details
                            </Menu.Item>
                            <Menu.Item key="edit" onClick={() => handleEdit(record)}>
                                <EditOutlined /> Edit
                            </Menu.Item>
                            <Menu.Item key="copy" onClick={() => handleDuplicate(record)}>
                                <CopyOutlined /> Duplicate
                            </Menu.Item>
                            <Menu.Divider />
                            <Menu.Item key="delete" danger>
                                <Popconfirm
                                    title="Are you sure you want to delete this material?"
                                    onConfirm={() => handleDelete(record.id)}
                                    okText="Yes"
                                    cancelText="No"
                                >
                                    <DeleteOutlined /> Delete
                                </Popconfirm>
                            </Menu.Item>
                        </Menu>
                    }
                    trigger={["click"]}
                >
                    <Button icon={<EllipsisOutlined />} size="small" />
                </Dropdown>
            ),
        },
    ];

    return (
        <div className="container-fluid mt-4">
            <div className="card shadow-sm">
                <div className="card-header bg-white py-3">
                    <Row align="middle" justify="space-between">
                        <Col>
                            <h4 className="mb-0">
                                Raw Materials - <span style={{ color: '#1890ff' }}>{businessName}</span>
                            </h4>
                        </Col>
                        <Col>
                            <Space>
                                <Button icon={<ReloadOutlined />} onClick={fetchRawMaterials}>
                                    Refresh
                                </Button>
                                <Button icon={<DownloadOutlined />} onClick={handleExport}>
                                    Export
                                </Button>
                                <Button 
                                    type="primary" 
                                    icon={<PlusOutlined />} 
                                    onClick={handleAdd}
                                >
                                    Add Material
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                </div>

                <div className="card-body">
                    <Space style={{ marginBottom: 16 }} wrap>
                        <Input.Search
                            placeholder="Search by name, code, description..."
                            value={searchText}
                            onChange={(e) => handleSearch(e.target.value)}
                            style={{ width: 350 }}
                            allowClear
                        />
                    </Space>

                    <Table
                        columns={columns}
                        dataSource={filteredMaterials}
                        rowKey="id"
                        loading={loading}
                        pag={{ 
                            pageSize: 10,
                            showSizeChanger: true,
                            showTotal: (total) => `Total ${total} materials`
                        }}
                        bordered
                        scroll={{ x: 1300 }}
                        size="middle"
                    />
                </div>
            </div>

            {/* Add/Edit Modal */}
            <Modal
                title={selectedMaterial ? "Edit Raw Material" : "Add New Raw Material"}
                open={isModalVisible}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
                okText={selectedMaterial ? "Update" : "Create"}
                cancelText="Cancel"
                confirmLoading={loading}
                width={700}
            >
                <Form form={form} layout="vertical">
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Material Code"
                                name="code"
                                rules={[{ required: true, message: 'Please enter material code!' }]}
                            >
                                <Input placeholder="e.g., RM-001" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Material Name"
                                name="name"
                                rules={[{ required: true, message: 'Please enter material name!' }]}
                            >
                                <Input placeholder="Enter material name" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        label="Description"
                        name="description"
                    >
                        <TextArea placeholder="Enter description" rows={3} />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Supplier Part Number"
                                name="supplierPartNumber"
                            >
                                <Input placeholder="Enter supplier part number" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Base Unit of Measure"
                                name="baseUnitOfMeasure"
                                rules={[{ required: true, message: 'Please select UOM!' }]}
                            >
                                <Select placeholder="Select UOM">
                                    {unitOfMeasureOptions.map(uom => (
                                        <Option key={uom} value={uom}>{uom}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        label="Estimated Unit Cost"
                        name="estimatedUnitCost"
                    >
                        <InputNumber
                            min={0}
                            step={0.01}
                            precision={2}
                            style={{ width: '100%' }}
                            placeholder="0.00"
                            formatter={value => `₵ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={value => value.replace(/₵\s?|(,*)/g, '')}
                        />
                    </Form.Item>
                </Form>
            </Modal>

            {/* View Details Modal */}
            <Modal
                title="Raw Material Details"
                open={isViewModalVisible}
                onCancel={() => setIsViewModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setIsViewModalVisible(false)}>
                        Close
                    </Button>
                ]}
                width={600}
            >
                {selectedMaterial && (
                    <div>
                        <p><strong>Code:</strong> {selectedMaterial.code}</p>
                        <p><strong>Name:</strong> {selectedMaterial.name}</p>
                        <p><strong>Description:</strong> {selectedMaterial.description || '-'}</p>
                        <p><strong>Supplier Part Number:</strong> {selectedMaterial.supplierPartNumber || '-'}</p>
                        <p><strong>Unit of Measure:</strong> {selectedMaterial.baseUnitOfMeasure}</p>
                        <p><strong>Estimated Unit Cost:</strong> ₵{selectedMaterial.estimatedUnitCost?.toFixed(2) || '0.00'}</p>
                        <p><strong>Date Created:</strong> {selectedMaterial.createdDate ? moment(selectedMaterial.createdDate).format('YYYY-MM-DD HH:mm') : '-'}</p>
                        {selectedMaterial.dateUpdated && (
                            <p><strong>Last Updated:</strong> {moment(selectedMaterial.dateUpdated).format('YYYY-MM-DD HH:mm')}</p>
                        )}
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default Rawmaterial;

